import React from "react";
import { Button, Form, Input, Select, Spin } from "antd";
import { DeleteIcon } from "../../../../../../../../Components/Icons/deleteIcon";
import { FormItemType } from "../../../../createDownline.constants";
import { FormItem, SelectedProductCommission } from "../../../../createDownline.models";
import { getFormRules, getInputValueFromEvent, getProductGroupRules } from "./section.utils";
import styles from "./section.module.css";

export interface SectionProps {
    title: string;
    formItems?: FormItem[];
    disabled?: boolean;
    productSectionConfig?: {
        selectedProducts: SelectedProductCommission[];
        productOptions: { label: string; value: string; disabled?: boolean }[];
        commissionOptions: { label: string; value: string }[];
        selectedProductUid?: string;
        canAddProduct: boolean;
        productAndCommissionsLoading: boolean;
        onOpenProductDropdown: (open: boolean) => void;
        onProductChange: () => void;
        onAddProduct: () => void;
        onRemoveProduct: (productUid: string) => void;
    };
}

export const Section = ({ title, formItems = [], disabled = false, productSectionConfig }: SectionProps) => {
    const renderFormLayout = () => {
        if (productSectionConfig) {
            return (
                <>
                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldError }) => {
                            const hasProductsError = getFieldError("products").length > 0;

                            return (
                                <div className={styles.formLayout}>
                                    <div className={styles.formItem}>
                                        <Form.Item
                                            className={styles.formAntdItem}
                                            name="selectedProductUid"
                                            label={<span className={styles.formItemLabel}>Product</span>}
                                            required
                                            validateStatus={hasProductsError ? "error" : undefined}
                                        >
                                            <Select
                                                placeholder="Select"
                                                className={styles.input}
                                                options={productSectionConfig.productOptions}
                                                loading={productSectionConfig.productAndCommissionsLoading}
                                                onOpenChange={productSectionConfig.onOpenProductDropdown}
                                                onChange={productSectionConfig.onProductChange}
                                                status={hasProductsError ? "error" : undefined}
                                                notFoundContent={
                                                    productSectionConfig.productAndCommissionsLoading ? (
                                                        <Spin size="small" className={styles.spin} />
                                                    ) : null
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className={styles.formItem}>
                                        <Form.Item
                                            className={styles.formAntdItem}
                                            label={<span className={styles.formItemLabel}>Commission Level</span>}
                                            required
                                            validateStatus={hasProductsError ? "error" : undefined}
                                        >
                                            <div className={styles.commissionSelect}>
                                                <Form.Item name="selectedCommissionUid" noStyle>
                                                    <Select
                                                        placeholder="Select"
                                                        className={styles.input}
                                                        options={productSectionConfig.commissionOptions}
                                                        disabled={!productSectionConfig.selectedProductUid}
                                                        status={hasProductsError ? "error" : undefined}
                                                    />
                                                </Form.Item>
                                                <Button
                                                    className={styles.rightContainer}
                                                    onClick={productSectionConfig.onAddProduct}
                                                    disabled={disabled || !productSectionConfig.canAddProduct}
                                                    variant="text"
                                                >
                                                    <span className={styles.addProductButtonText}>
                                                        Add
                                                    </span>
                                                </Button>
                                            </div>
                                        </Form.Item>
                                    </div>
                                </div>
                            );
                        }}
                    </Form.Item>
                    <Form.Item name="products" hidden rules={getProductGroupRules()} />
                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldError }) => {
                            const productErrors = getFieldError("products");
                            if (!productErrors.length) return null;

                            return <div className={styles.validationError}>{productErrors[0]}</div>;
                        }}
                    </Form.Item>
                </>
            );
        }

        return (
            <div className={styles.formLayout}>
                {formItems.map((item) => (
                    <div className={styles.formItem} key={item.field + item.type}>
                        <Form.Item
                            className={styles.formAntdItem}
                            name={item.field}
                            label={<span className={styles.formItemLabel}>{item.label}</span>}
                            rules={getFormRules(item)}
                            getValueFromEvent={getInputValueFromEvent(item.type)}
                        >
                            {item.type === FormItemType.SELECT ? (
                                <Select
                                    placeholder={item.placeholder}
                                    className={styles.input}
                                    options={item.options}
                                    disabled={item.isDisabled}
                                    loading={item.loading}
                                    onOpenChange={item.onOpenChange}
                                    notFoundContent={
                                        item.loading ? <Spin size="small" className={styles.spin} /> : null
                                    }
                                />
                            ) : (
                                <Input
                                    placeholder={item.placeholder}
                                    className={styles.input}
                                    classNames={{
                                        input: styles.inputField,
                                    }}
                                    type={item.type}
                                    disabled={item.isDisabled}
                                    allowClear
                                    maxLength={item.type === FormItemType.TEL ? 12 : undefined}
                                    inputMode={item.type === FormItemType.TEL ? "numeric" : undefined}
                                />
                            )}
                        </Form.Item>
                    </div>
                ))}
            </div>
        );
    };

    const renderProductPreview = () => {
        if (!productSectionConfig) return null;

        if (productSectionConfig.selectedProducts.length === 0) return null;

        return (
            <div className={styles.productRows}>
                {productSectionConfig.selectedProducts.map((item) => (
                    <div className={styles.productRow} key={item.productUid}>
                        <div className={styles.productRowContent}>
                            <span className={styles.productName}>{item.productName}</span>
                            <span className={styles.commissionName}>
                                Commission Level: {item.commissionName}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.deleteButton}
                            disabled={disabled}
                            onClick={() => productSectionConfig.onRemoveProduct(item.productUid)}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                <span className={styles.titleText}>{title}</span>
            </div>
            {renderFormLayout()}
            {renderProductPreview()}
        </div>
    );
};
